<template>
  <b-modal
    :id="modalID"
    ref="modal"
    size="lg"
    body-class="p-0"
    @hide="closeModal"
  >
    <template #modal-header={cancel}>
      <div class="w-100 px-5">
        <div class="d-flex justify-content-between pb-10">
          <div class="modal-title d-flex align-items-center">
            <TitleModal :label="$t('engine.modal.addlink.lblTitle', languageEngine)" />
          </div>
          <div class="d-flex align-items-center">
            <div
              type="button"
              aria-label="Close"
              @click="cancel()"
              id="close-modal-manage-header-menu"
              class="ml-5 close-modal"
            >
              <i class="fal fa-times"></i>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div class="px-gd-5 d-flex">
      <div class="pt-gd-2 pr-gd-2">
        <h5 class="fs-dbb-20 mb-0">
          {{ $t("engine.modal.addlink.lblLinkType", languageEngine) }}
        </h5>
        <b-form-group>
          <template v-for="type in _linkTypes">
            <b-form-radio 
              :key="type.value" 
              class="pt-gd-1 cursor-pointer" 
              v-model="linkType" 
              :value="type.value"
            >
              <p class="fs-sa-14 mb-0 cursor-pointer">
                {{ type.text }}
              </p>
            </b-form-radio>
          </template>
        </b-form-group>
      </div>

      <div class="col pt-gd-2" style="border-left: solid 1px #dee3e6">
        <h5 class="fs-dbb-20 mb-0">
          {{ $t("engine.modal.addlink.lblSetting", languageEngine) }}
        </h5>

        <div id="url" class="pt-gd-3" v-if="linkType === 'url'">
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblUrl', languageEngine)"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <b-form-input placeholder="https://" v-model="url" />
            <div :class="{ errors: (error && error != '') }">
              <small v-if="error == 'url_error'">{{ $t("engine.modal.addlink.msgUrlError", languageEngine) }}</small>
              <small v-if="error == 'url_format_error'">{{ $t("engine.modal.addlink.msgUrlFormatError", languageEngine) }}</small>
            </div>
          </FormGroupHorizontal>
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblOpenInNewWindow', languageEngine)"
            :labelClass="'align-self-center'"
            :contentClass="'align-self-center'"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <b-form-checkbox
              v-model="target"
              value="_blank"
              unchecked-value="_self"
            />
          </FormGroupHorizontal>
        </div>

        <div id="page" class="pt-gd-3" v-if="linkType === 'page'">
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblPage', languageEngine)"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <SelectPage v-model="pageID" class="w-100" />
            <div :class="{ errors: (error && error != '') }">
              <small v-if="error == 'page_error'">{{ $t("engine.modal.addlink.msgPageError", languageEngine) }}</small>
            </div>
          </FormGroupHorizontal>
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblOpenInNewWindow', languageEngine)"
            :labelClass="'align-self-center'"
            :contentClass="'align-self-center'"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <b-form-checkbox
              v-model="target"
              value="_blank"
              unchecked-value="_self"
            />
          </FormGroupHorizontal>
        </div>

        <div id="anchor" class="pt-gd-3" v-if="linkType === 'anchor'">
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblPage', languageEngine)"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <SelectPage v-model="sectionPageID" class="w-100" />
            <div :class="{ errors: (error && error != '') }">
              <small v-if="error == 'anchor_page_error'">{{ $t("engine.modal.addlink.msgAnchorPageError", languageEngine) }}</small>
            </div>
          </FormGroupHorizontal>
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblSection', languageEngine)"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <SelectSection
              v-model="sectionID"
              :pageId="sectionPageID"
              class="w-100"
            />
            <div :class="{ errors: (error && error != '') }">
              <small v-if="
                error == 'anchor_page_error' ||
                error == 'anchor_section_error'
              ">{{ $t("engine.modal.addlink.msgAnchorSectionError", languageEngine) }}</small>
            </div>
          </FormGroupHorizontal>
        </div>

        <div id="phoneNumber" class="mh-100 pt-gd-3" v-if="linkType === 'phone'">
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblPhoneNumber', languageEngine)"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <VuePhoneNumberInput
              class="fs-sa-14 mb-0"
              v-model="phone"
              @update="phoneUpdated"
              color="#24BEE2"
              :default-country-code="phoneCountry"
              show-code-on-list
              :translations = "phoneNumberOptions"
            />
            <div class="fs-sa-12 text-danger mb-0" v-if="error == 'phone_error'">
              {{ $t("engine.modal.addlink.msgPhoneError", languageEngine) }}
            </div>
            <div class="fs-sa-12 text-danger mb-0" v-if="error == 'phone_format_error'" >
              {{ $t("engine.modal.addlink.msgPhoneFormatError", languageEngine) }}
            </div>
          </FormGroupHorizontal>
          <div class="fs-sa-12 text-muted mb-0 align-items-end" style="position: absolute; bottom: 10px">
            {{ $t("engine.modal.addlink.msgPhone", languageEngine) }}
          </div>
        </div>

        <div id="email" class="pt-gd-3" v-if="linkType === 'email'">
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblEmailAddress', languageEngine)"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <b-form-input :placeholder="$t('engine.modal.addlink.setting.emailAddress.lblPlaceholder', languageEngine)" v-model="email" />
            <div class="fs-sa-12 text-danger mb-0" v-if="error == 'email_error' || error == 'email_and_subject_error'">
              {{ $t("engine.modal.addlink.msgEmailError", languageEngine) }}
            </div>
            <div class="fs-sa-12 text-danger mb-0" v-if="error == 'email_format_error'">
              {{ $t("engine.modal.addlink.msgEmailFormatError", languageEngine) }}
            </div>
          </FormGroupHorizontal>
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblEmailSubject', languageEngine)"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <b-form-input :placeholder="$t('engine.modal.addlink.setting.emailSubject.lblPlaceholder', languageEngine)" v-model="emailSubject"/>
            <div class="fs-sa-12 text-danger mb-0" v-if="error == 'email_subject_error' || error == 'email_and_subject_error'">
              {{ $t("engine.modal.addlink.msgEmailSubjectError", languageEngine) }}
            </div>
          </FormGroupHorizontal>
        </div>

        <div id="file" class="pt-gd-3" v-if="linkType === 'document' || linkType === 'file'">
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblChooseFile', languageEngine)"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <b-button variant="default" @click="openModalFile">
              <i class="far fa-image mr-2"></i>
              {{ $t("engine.modal.addlink.setting.btnBrowse", languageEngine) }}
            </b-button>
            <div class="fs-sa-12 text-danger mb-0" v-if="error == 'file_error'">
              {{ $t("engine.modal.addlink.msgFileError", languageEngine) }}
            </div>
          </FormGroupHorizontal>
          <FormGroupHorizontal 
            class="mt-gd-2-nfc"
            :label="$t('engine.modal.addlink.setting.lblFileName', languageEngine)"
            :split="'4:8'"
            :paddingClosest="'8'"
          >
            <div class="fs-dbb-20 mt-5" style="word-break: break-all;">{{ file.name }}</div>
          </FormGroupHorizontal>
        </div>
      </div>
    </div>

    <template #modal-footer={cancel}>
      <div class="mx-gd-2">
        <b-button variant="blue" :disabled="error != '' ? true : false" class="mr-gd-1" @click="setLink"
          ><i class="far fa-check"></i> {{ $t("engine.modal.addlink.setting.btnSave", languageEngine) }} </b-button
        >
        <b-button variant="light" @click="cancel">
          <i class="far fa-times"></i> {{ $t("engine.modal.addlink.setting.btnCancel", languageEngine) }} </b-button
        >
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import SelectPage from "../AddLink/SelectPage.vue";
import SelectSection from "../AddLink/SelectSection.vue";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import TitleModal from "@components_system/Shared/Elements/Texts/TitleModal";
import { email } from "vuelidate/lib/validators";
import FormGroupHorizontal from '@components_system/Shared/Tools/FormGroupHorizontal/FormGroupHorizontal';

export default {
  components: {
    SelectPage,
    SelectSection,
    VuePhoneNumberInput,
    TitleModal,
    FormGroupHorizontal,
  },
  data() {
    return {
      modalID: "modal-add-link-widget-tools",

      linkType: "page",
      phone: "",
      phoneCountry: "TH",
      phoneFormatted: "",
      phoneIsValid: true,
      email: "",
      emailSubject: "",
      url: "",
      target: "_blank",
      pageID: "",
      sectionPageID: "",
      sectionID: "",
      file: {},

      error: "",
      isShowError: false,

      phoneNumberOptions: {
        phoneNumberLabel: this.$t('engine.modal.addlink.setting.phoneNumber.lblPlaceholder', this.languageEngine),
        example: this.$t('engine.modal.addlink.setting.phoneNumber.lblExample', this.languageEngine)
      }
    };
  },
  computed: {
    ...mapGetters({
      modals: "design/modals/modals",
      modalsID: "design/modals/modalsID",
      modalActive: "design/modals/modalActive",
      languageEngine: "design/userPreference/languageEngine",
    }),
    modal() {
      if (!this.modalsID.includes(this.modalID)) return {};
      return this.modals[this.modalID];
    },
    data() {
      return this.modal && this.modal.payload;
    },
    config() {
      return this.modal && this.modal.payload && this.modal.payload.config;
    },

    linkTypes() {
      return [
        { value : 'url', text : this.$t("engine.modal.addlink.linkType.lblUrl", this.languageEngine) },
        { value : 'page', text : this.$t("engine.modal.addlink.linkType.lblPage", this.languageEngine) },
        { value : 'anchor', text : this.$t("engine.modal.addlink.linkType.lblAnchor", this.languageEngine) },
        { value : 'phone', text : this.$t("engine.modal.addlink.linkType.lblPhoneNumber", this.languageEngine) },
        { value : 'email', text : this.$t("engine.modal.addlink.linkType.lblEmail", this.languageEngine) },
        { value : 'file', text : this.$t("engine.modal.addlink.linkType.lblDocument", this.languageEngine) },
      ]
    },
    _linkTypes() { 
      let types = this.linkTypes;
      if(this.config && this.config.linkTypes) {
        types = this.linkTypes.filter((type) => this.config.linkTypes.find(type2 => type2 == type.value )) 
      }
      return types;
    },
  },
  methods: {
    closeModal() {
      this.$store.dispatch("design/modals/callback", {
        key: this.modalID,
        clear : true,
        func : 'cancel',
        data : {}
      }, { root:true });
      this.clear();
      this.$refs.modal.hide();
    },
    init() {
      this.linkType = (this.data && this.data.type) || "url";
      this.url = (this.data && this.data.url) || "";
      this.target = (this.data && this.data.target) || "_blank";

      this.phoneCountry = (this.data && this.data.phoneCountry) || "TH";
      this.phoneFormatted = (this.data && this.data.phoneFormat) || "";
      this.phone = (this.data && this.data.phone) || null;

      this.email = (this.data && this.data.email) || "";
      this.emailSubject = (this.data && this.data.emailSubject) || "";

      this.pageID = (this.data && this.data.pageID) || "";
      this.sectionPageID = (this.data && this.data.sectionPageID) || "";
      this.sectionID = (this.data && this.data.sectionID) || "";

      this.file.url = (this.data && this.data.fileUrl) || "";
      this.file.name = (this.data && this.data.fileName) || "";
      this.file.id = (this.data && this.data.fileID) || "";

      this.$refs.modal.show();
    },
    reset() {
      this.url = (this.data && this.data.url) || "";
      this.target = (this.data && this.data.target) || "_blank";

      this.email = (this.data && this.data.email) || "";
      this.emailSubject = (this.data && this.data.emailSubject) || "";

      this.pageID = (this.data && this.data.pageID) || "";
      this.sectionPageID = (this.data && this.data.sectionPageID) || "";
      this.sectionID = (this.data && this.data.sectionID) || "";

      this.phone = (this.data && this.data.phone) || null;
      this.phoneCountry = (this.data && this.data.phoneCountry) || "TH";
      this.phoneFormatted = (this.data && this.data.phoneFormat) || "";

      this.file.url = (this.data && this.data.fileUrl) || "";
      this.file.name = (this.data && this.data.fileName) || "";
      this.file.id = (this.data && this.data.fileID) || "";
    },
    clear() {
      this.linkType = "url";
      this.target  = "_blank";
      this.url = "";

      this.email = "";
      this.emailSubject = "";

      this.pageID = "";
      this.sectionPageID = "";
      this.sectionID = "";

      this.phone = null;
      this.phoneCountry  = "TH";
      this.phoneFormatted = "";

      this.file.url = "";
      this.file.name = "";
      this.file.id = "";
    },
    phoneUpdated($e) {
      this.phone = $e.phoneNumber || null;
      this.phoneCountry = $e.countryCode
      this.phoneFormatted = $e.formattedNumber
      this.phoneIsValid = $e.isValid;
    },
    setLink() {
      this.$v.$touch();
      switch (this.linkType) {
        case 'phone':
          this.setLinkPhone()
          break;
        case 'email':
          this.setLinkEmail()
          break;
        case 'url':
          this.setLinkUrl()
          break;
        case 'file':
          this.setLinkDocument()
          break;
        case 'page':
          this.setLinkPage()
          break;
        default:
          this.setLinkAnchor();
          break;
      }
    },
    setLinkPhone() {
      if(this.phone) {
        if (this.phoneIsValid) {
          this.save();
        }else {
          this.error = "phone_format_error";
        }
      }else {
        this.error = "phone_error";
      }
    },
    setLinkEmail() {
      if (this.email && this.emailSubject) {
        this.$v.$touch();
        if (this.$v.email.$invalid) {
          this.error = "email_format_error";
          this.isShowError = true;
        } else {
          this.save();
        }
      } else if (!this.email && this.emailSubject){
        this.error = "email_error";
        this.isShowError = true;
      } else if (this.email && !this.emailSubject) {
        this.error = "email_subject_error";
        this.isShowError = true;
      } else if(!this.email && !this.emailSubject){
        this.error = "email_and_subject_error";
        this.isShowError = true;
      }
    },
    setLinkUrl() {
      if (this.url) { 
        if (this.$helper.url.isValidUrl(this.url)) {
          this.save();
        } else {
          this.error = "url_format_error";
          this.isShowError = true;
        }
      } else {
        this.error = "url_error";
        this.isShowError = true;
      }
    },
    setLinkPage() {
      if (this.pageID) {
        this.save();
      } else {
        this.error = "page_error";
      }
    },
    setLinkAnchor() {
      if (this.sectionPageID && this.sectionID) {
        this.save();
      }else {
        if (!this.sectionPageID) {
          this.error = "anchor_page_error";
        } else {
          this.error = "anchor_section_error";
        }
      }
    },
    setLinkDocument() {
      if (this.file.url && this.file.name) { 
        this.save();
      } else {
        this.error = "file_error";
      }
    },
    setDocument(files) {
      this.file = {
        name: files[0].name,
        url: files[0].url,
        id: files[0].id,
      };
    },
    openModalFile() {
      this.$store.dispatch("design/openFilemanager", {
        onChange: this.setDocument,
        isShowDocument: true,
        isShowImage: false,
        isShowUnsplash: false,
        isShowFlaticon: false,
        isShowIcon: false,
      });
    },
    save() {
      this.$store.dispatch("design/modals/callback", {
        key: this.modalID,
        clear : true,
        func : 'save',
        data : {
          linkType: this.linkType, // will delete
          type: this.linkType,
          phone: this.phone,
          phoneCountry: this.phoneCountry,
          phoneFormat: this.phoneFormatted,
          email: this.email,
          emailSubject: this.emailSubject,
          url: this.url,
          target: this.target,
          pageID: this.pageID,
          sectionPageID: this.sectionPageID,
          sectionID: this.sectionID,
          file: this.file, // will delete
          fileID: this.file.id
        }
      },{ root:true });
      this.closeModal();
    }
  },
  validations: {
    email: {
      email,
    },
  },
  beforeDestroy() {
    this.closeModal();
  },
  watch: {
    modalActive: {
      handler(next) {
        if (next == this.modalID) {
          this.init();
        }
      },
    },
    linkType: {
      handler() {
        this.reset();
        this.error = "";
        this.$v.$reset();
      },
    },
    url: {
      handler() {
        this.error = "";
      },
    },
    pageID: {
      handler() {
        this.error = "";
      },
    },
    sectionPageID: {
      handler() {
        this.error = "";
      },
    },
    sectionID: {
      handler() {
        this.error = "";
      },
    },
    phone: {
      handler() {
        this.error = "";
      },
    },
    phoneCountry: {
      handler() {
        this.error = "";
      },
    },
    phoneFormatted: {
      handler() {
        this.error = "";
      },
    },
    email: {
      handler() {
        this.error = "";
      },
    },
    emailSubject: {
      handler() {
        this.error = "";
      },
    },
    file: {
      deep: true,
      handler() {
        this.error = "";
      },
    },
  },
};
</script>

<style scoped>
/* .error:focus {
  border: solid 1px #D9534F;
  box-shadow: 0 0 4px 2px #D9534F;
} */
.vue-phone-number-input >>> .input-country-selector {
  width: 100px !important;
}
.vue-phone-number-input >>> .country-selector__input {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  padding-top: 0 !important;
  font-family: Tahoma, sans-serif, Arial;
  font-size: 14px;
  color: #373e44;
}
.vue-phone-number-input >>> .country-selector__list__item {
  font-family: Tahoma, sans-serif, Arial;
  font-size: 14px;
  color: #373e44;
}
.vue-phone-number-input >>> .country-selector__list__item:hover {
  background-color: #e4faff;
}
.vue-phone-number-input >>> .country-selector__list__item.selected {
  font-weight: normal;
  background-color: #24bee2;
}
.vue-phone-number-input >>> .country-selector__label {
  display: none;
}
.vue-phone-number-input >>> .country-selector__country-flag {
  top: 50%;
  transform: translateY(-50%);
}

.vue-phone-number-input
  >>> .input-tel__input[data-v-e59be3b4]:not(.no-country-selector) {
  margin-left: -10px;
  border-top-left-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
}
</style>


